import { serverOnly$, clientOnly$ } from 'vite-env-only/macros';
import * as z from 'zod';

export const environmentSchema = z.object({
    PRELAUNCH: z.enum([ 'true', 'false' ]).default('false')
});

export type PublicEnvironment = z.infer<typeof environmentSchema>;
declare global {
    interface Window {
        ENV: PublicEnvironment;
    }
}

export function createEnvironment<S extends z.ZodSchema>(environmentSchema: S): {
    environment: () => z.infer<typeof environmentSchema>;
    PublicEnv: React.FC<z.infer<typeof environmentSchema>>;
    getPublicEnv: <T extends keyof z.infer<typeof environmentSchema>>(key: T) => z.infer<typeof environmentSchema>[T];
    getAllPublicEnv: () => z.infer<typeof environmentSchema>;
} {
    const environment = () => {
        const result = serverOnly$(environmentSchema.parse(process.env));
        if (!result) {
            if (clientOnly$(true)) {
                throw new Error("Don't use this in the client! Always use getPublicEnv() instead!");
            }
            throw new Error('Error parsing environment variables!');
        }
        return result;
    };

    return {
        environment,
        PublicEnv: function PublicEnv(env: z.infer<typeof environmentSchema>) {
            return (
                <script
                    dangerouslySetInnerHTML={{
                        __html: `window.ENV = ${JSON.stringify(env)}`
                    }}
                />
            );
        },
        getPublicEnv: function getPublicEnv<T extends keyof z.infer<typeof environmentSchema>>(
            key: T,
        ): z.infer<typeof environmentSchema>[T] {
            if (typeof window !== 'undefined') {
                if (!window.ENV) {
                    throw new Error(
                        `Missing the <PublicEnv /> component at the root of your app.`
                    );
                }
                return (window.ENV as any)[key];
            }
            return environment()[key];
        },
        getAllPublicEnv: function getAllPublicEnv(): z.infer<typeof environmentSchema> {
            if (typeof window !== 'undefined') {
                if (!window.ENV) {
                    throw new Error(
                        `Missing the <PublicEnv /> component at the root of your app.`
                    );
                }
                return window.ENV as z.infer<typeof environmentSchema>;
            }
            return environment();
        }
    };
}
